import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    EllipsisHorizontalIcon,
    XMarkIcon,
    PencilIcon,
    ArchiveBoxIcon,
    ArchiveBoxXMarkIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '@/components/ui/Spinner';
import { useUpdateLedger, useDeleteLedger } from '@/api/hooks/useLedgers';
import { useBatchEditFormData } from '@/api/hooks/useBatchEditFormData';
import { useAuth } from '@/contexts/AuthContext';
import { StickyBackBar } from '@/components/layout/StickyBackBar';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { LedgerForm } from '@/components/ledgers/LedgerForm';
import { ArchiveConfirmDialog } from '@/components/ledgers/ArchiveConfirmDialog';
import { RestoreConfirmDialog } from '@/components/ledgers/RestoreConfirmDialog';
import { toast } from 'sonner';
import type { Ledger, UpdateLedgerData } from '@/api/types';
import { isLedgerArchived } from '@/utils/ledger';

interface LedgerDetailBarProps {
    destinationLabel: string;
    backPath: string;
    ledger: Ledger;
}

/**
 * Sticky back bar with ledger-specific actions.
 * Shows "Back to Assets" or "Back to Debts" on the left.
 * Shows ledger action buttons (...) on the right.
 *
 * Collections and tags for the edit form are lazy-loaded only when Edit is clicked.
 */
export function LedgerDetailBar({
    destinationLabel,
    backPath,
    ledger,
}: LedgerDetailBarProps) {
    const navigate = useNavigate();
    const { settings } = useAuth();

    // Actions expanded state
    const [isActionsExpanded, setIsActionsExpanded] = useState(false);

    // Dialog state
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
    const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);

    // Lazy-load collections/tags only when edit dialog is open
    const [editFormDataEnabled, setEditFormDataEnabled] = useState(false);
    const { data: editFormData, isLoading: isLoadingEditData } = useBatchEditFormData({
        type: ledger.type,
        enabled: editFormDataEnabled,
    });

    // Mutations
    const updateLedger = useUpdateLedger();
    const deleteLedger = useDeleteLedger();

    const isArchived = isLedgerArchived(ledger);
    const typeLabel = ledger.type === 'asset' ? 'Asset' : 'Debt';
    const listPath = ledger.type === 'asset' ? '/assets' : '/debts';

    const handleToggleActions = () => {
        setIsActionsExpanded(!isActionsExpanded);
    };

    const handleEdit = () => {
        setIsActionsExpanded(false);
        setEditFormDataEnabled(true); // Trigger lazy load of collections/tags
        setIsEditDialogOpen(true);
    };

    const handleEditSubmit = async (data: UpdateLedgerData) => {
        try {
            await updateLedger.mutateAsync({ id: ledger.id, data });
            setIsEditDialogOpen(false);
            toast.success(`${typeLabel} updated`);
        } catch {
            toast.error(`Failed to update ${typeLabel.toLowerCase()}`);
        }
    };

    const handleArchiveClick = () => {
        setIsActionsExpanded(false);
        if (isArchived) {
            setIsRestoreDialogOpen(true);
        } else {
            setIsArchiveDialogOpen(true);
        }
    };

    const handleDeleteClick = () => {
        setIsActionsExpanded(false);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteLedger.mutateAsync(ledger.id);
            toast.success(`${typeLabel} deleted`);
            navigate(listPath);
        } catch {
            toast.error(`Failed to delete ${typeLabel.toLowerCase()}`);
        }
    };

    const isLoading = updateLedger.isPending || deleteLedger.isPending;

    // Shared button styles for flat toolbar design
    const actionButtonBase = "inline-flex items-center gap-2 px-4 py-2 text-base md:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
    const actionButtonDefault = "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700";
    const actionButtonDanger = "text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20";

    return (
        <>
            <StickyBackBar destinationLabel={destinationLabel} backPath={backPath}>
                {/* Toggle button - always rendered to maintain space, invisible when expanded */}
                <button
                    onClick={handleToggleActions}
                    className={`${actionButtonBase} ${actionButtonDefault} border border-gray-200 dark:border-gray-700 rounded-lg ${isActionsExpanded ? 'invisible' : ''}`}
                    aria-label="Ledger actions"
                    title="Actions"
                >
                    <EllipsisHorizontalIcon className="h-5 w-5" />
                </button>
                {/* Expanded toolbar - absolute positioned over the toggle */}
                {isActionsExpanded && (
                <div className="absolute right-0 top-0 flex items-center border border-gray-200 dark:border-gray-700 bg-white dark:bg-darker rounded-lg overflow-hidden shadow-lg">
                    <button
                        onClick={handleEdit}
                        disabled={isLoading}
                        className={`${actionButtonBase} ${actionButtonDefault}`}
                        aria-label="Edit ledger"
                        title="Edit"
                    >
                        <PencilIcon className="h-5 w-5" />
                        <span className="hidden sm:inline">Edit</span>
                    </button>
                    <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
                    <button
                        onClick={handleArchiveClick}
                        disabled={isLoading}
                        className={`${actionButtonBase} ${actionButtonDefault}`}
                        aria-label={isArchived ? 'Restore ledger' : 'Archive ledger'}
                        title={isArchived ? 'Restore' : 'Archive'}
                    >
                        {isArchived ? (
                            <ArchiveBoxXMarkIcon className="h-5 w-5" />
                        ) : (
                            <ArchiveBoxIcon className="h-5 w-5" />
                        )}
                        <span className="hidden sm:inline">{isArchived ? 'Restore' : 'Archive'}</span>
                    </button>
                    <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
                    <button
                        onClick={handleDeleteClick}
                        disabled={isLoading}
                        className={`${actionButtonBase} ${actionButtonDanger}`}
                        aria-label="Delete ledger"
                        title="Delete"
                    >
                        <TrashIcon className="h-5 w-5" />
                        <span className="hidden sm:inline">Delete</span>
                    </button>
                    <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
                    <button
                        onClick={handleToggleActions}
                        className={`${actionButtonBase} ${actionButtonDefault}`}
                        aria-label="Close actions"
                        title="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                )}
            </StickyBackBar>

            {/* Edit Dialog */}
            <Dialog
                open={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                title={`Edit ${typeLabel}`}
                size="2xl"
            >
                {isLoadingEditData ? (
                    <div className="flex justify-center py-8">
                        <div className="flex items-center space-x-2">
                            <Spinner />
                            <span className="text-gray-500 dark:text-gray-400">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <LedgerForm
                        type={ledger.type}
                        collections={editFormData?.collections ?? []}
                        tags={editFormData?.tags ?? []}
                        ledger={ledger}
                        onSubmit={handleEditSubmit}
                        onCancel={() => setIsEditDialogOpen(false)}
                        isLoading={updateLedger.isPending}
                        defaultCurrency={settings?.displayCurrency || 'USD'}
                    />
                )}
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                title={`Delete ${typeLabel}`}
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to delete the {typeLabel.toLowerCase()}{' '}
                        <strong className="font-semibold text-gray-900 dark:text-gray-100">
                            {ledger.label}
                        </strong>
                        ? This will also delete all associated value history. This action cannot be undone.
                    </p>

                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setIsDeleteDialogOpen(false)}
                            disabled={deleteLedger.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteConfirm}
                            isLoading={deleteLedger.isPending}
                        >
                            Delete {typeLabel}
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Archive Confirmation Dialog */}
            <ArchiveConfirmDialog
                ledger={ledger}
                isOpen={isArchiveDialogOpen}
                onClose={() => setIsArchiveDialogOpen(false)}
            />

            {/* Restore Confirmation Dialog */}
            <RestoreConfirmDialog
                ledger={ledger}
                isOpen={isRestoreDialogOpen}
                onClose={() => setIsRestoreDialogOpen(false)}
            />
        </>
    );
}
